import { useEffect, useRef, useState } from 'react';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import { useModal } from '@/hooks/use-modal';
import { needsReportCreationConfirmation } from '../utils/reportSaveGuards';
import type { ReportData } from '@/components/preview/types';

/** The modal takes ~250ms to be safe to mount — see `OPEN_DELAY` below. */
const OPEN_DELAY = 250;

interface UseConfirmReportCreationOptions {
    formData: ReportData;
    reportId: string | null;
    isLoading: boolean;
    /**
     * The product tour paints above the dialog overlay, so a modal opened while
     * it runs would be unreachable. Pass whether the tour is out of the way.
     */
    canOpenModal: boolean;
    /** Creates the report. Runs on confirm. */
    onConfirm: () => Promise<void> | void;
    /** Clears the chosen patient. Runs on deny and on dismissal. */
    onCancel: () => void;
}

interface PendingPrompt {
    patientId: string;
    /** Set once the modal was actually observed open, so dismissal is detectable. */
    opened: boolean;
    answered: boolean;
}

/**
 * Turns "choose a patient" into "create the report", by asking about it.
 *
 * A blank report cannot be saved before it has a patient, and most of the
 * editor cannot be used before the report is saved — so the moment a patient
 * lands on an unsaved report is the moment the whole editor is waiting on. This
 * hook watches for it, from wherever the patient came (the dropdown, the new
 * patient dialog, or a navigation that carried one along), asks once, and
 * either creates the report or puts the choice back.
 *
 * The pending state it returns is what the rest of the editor coordinates on:
 * nothing else may create the report while the question is on screen.
 */
export const useConfirmReportCreation = ({
    formData,
    reportId,
    isLoading,
    canOpenModal,
    onConfirm,
    onCancel,
}: UseConfirmReportCreationOptions) => {
    const { openModal, closeModal, isOpen } = useModal();
    const [answeredForPatientId, setAnsweredForPatientId] = useState<string | null>(null);
    const promptRef = useRef<PendingPrompt | null>(null);

    const patientId = formData.patient?.id ?? null;
    const patientName = formData.patient?.full_name;
    const patientAvatarUrl = formData.patient?.avatar_url;

    const isPendingPatientConfirmation = needsReportCreationConfirmation({
        report: formData,
        reportId,
        isLoading,
        answeredForPatientId,
    });

    const onConfirmRef = useRef(onConfirm);
    const onCancelRef = useRef(onCancel);
    onConfirmRef.current = onConfirm;
    onCancelRef.current = onCancel;

    useEffect(() => {
        if (!isPendingPatientConfirmation || !canOpenModal || !patientId) return;
        if (promptRef.current?.patientId === patientId) return;

        promptRef.current = { patientId, opened: false, answered: false };

        // The "add a new patient" path closes its own dialog in the same tick.
        // Mounting this one over a Radix dialog that is still unmounting leaves
        // `pointer-events: none` behind on the body, and the buttons below stop
        // responding — so let the other one finish leaving first.
        const timer = window.setTimeout(() => {
            openModal({
                // The face beside the name is the confirmation: this modal asks
                // whether this is the right patient. Falls back to initials when
                // the photo has not been generated yet.
                icon: (
                    <PatientAvatar
                        name={patientName ?? 'Paciente'}
                        avatarUrl={patientAvatarUrl}
                        className="size-11"
                        fallbackClassName="text-sm"
                    />
                ),
                title: patientName ? `Criar o laudo para ${patientName}?` : 'Criar o laudo?',
                description: 'Depois de criado, o paciente não pode ser trocado neste laudo.',
                content: null,
                // The question lives entirely in the title and description, so
                // drop the body's padding rather than leave an empty band above
                // the buttons.
                bodyClassName: 'p-0',
                showCloseButton: false,
                footer: {
                    closeText: 'Escolher outro',
                    closeVariant: 'outline',
                    acceptText: 'Criar laudo',
                    onClose: () => {
                        const prompt = promptRef.current;
                        if (prompt) prompt.answered = true;
                        // Reset instead of pinning the id, so picking the same
                        // patient again asks again.
                        promptRef.current = null;
                        onCancelRef.current();
                    },
                    onAccept: (close) => {
                        const prompt = promptRef.current;
                        if (prompt) prompt.answered = true;
                        setAnsweredForPatientId(patientId);
                        close();
                        void onConfirmRef.current();
                    },
                },
            });
        }, OPEN_DELAY);

        return () => window.clearTimeout(timer);
    }, [
        canOpenModal,
        isPendingPatientConfirmation,
        openModal,
        patientAvatarUrl,
        patientId,
        patientName,
    ]);

    // Escape and the backdrop go straight to `closeModal` and never reach the
    // footer's own handler, so dismissal has to be read off the modal closing.
    // Without this the user is left holding a patient on a report that will
    // never be created, with the autosave held back and the question gone.
    useEffect(() => {
        const prompt = promptRef.current;
        if (!prompt || prompt.answered) return;

        if (isOpen) {
            prompt.opened = true;
            return;
        }

        if (prompt.opened) {
            promptRef.current = null;
            onCancelRef.current();
        }
    }, [isOpen]);

    // The report can also be created from the header's save button or Ctrl+S.
    // Record that as the answer, so no prompt is left hanging over it.
    useEffect(() => {
        if (!formData.id || !patientId) return;

        promptRef.current = null;
        setAnsweredForPatientId((current) => (current === patientId ? current : patientId));
    }, [formData.id, patientId]);

    useEffect(() => {
        return () => {
            // A prompt still on screen when the editor unmounts would otherwise
            // outlive the report it asks about.
            if (promptRef.current && !promptRef.current.answered) closeModal();
        };
    }, [closeModal]);

    return { isPendingPatientConfirmation };
};
